import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api/client';
import type { PagedResult } from '@/api/client';
import type { PaymentClaim, PaymentInfo } from '@/api/payments';
import type {
  ApproveClaimRequest,
  ApproveClaimResponse,
  DemoSeedResult,
  ExtendSubscriptionRequest,
  PlatformAuditEntry,
  PlatformStats,
  RejectClaimRequest,
  TenantDetail,
  TenantIsolation,
  TenantListItem,
  UnverifiedUser,
  UpdatePaymentSettingsRequest,
  UpdateTenantRequest,
  UpdateTenantStatusRequest,
} from '@/api/superadmin';

// ── Tenants ──────────────────────────────────────────────────────────────────

interface TenantsQuery {
  search?: string;
  status?: string;
  plan?: string;
  page?: number;
  limit?: number;
  [key: string]: string | number | boolean | null | undefined;
}

export function useTenants(query: TenantsQuery = {}) {
  return useQuery({
    queryKey: ['superadmin', 'pharmacies', query],
    queryFn: () => api.get<PagedResult<TenantListItem>>('/api/superadmin/pharmacies', query),
  });
}

export function useTenantDetail(id: string | null) {
  return useQuery({
    queryKey: ['superadmin', 'pharmacies', id],
    queryFn: () => api.get<TenantDetail>(`/api/superadmin/pharmacies/${id}`),
    enabled: !!id,
  });
}

function invalidateTenants(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['superadmin', 'pharmacies'] });
}

export function useUpdateTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: UpdateTenantRequest }) =>
      api.put<TenantListItem>(`/api/superadmin/pharmacies/${id}`, request),
    onSuccess: () => invalidateTenants(queryClient),
  });
}

export function useUpdateTenantStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: UpdateTenantStatusRequest }) =>
      api.put<TenantListItem>(`/api/superadmin/pharmacies/${id}/status`, request),
    onSuccess: () => invalidateTenants(queryClient),
  });
}

export function useExtendSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: ExtendSubscriptionRequest }) =>
      api.put<TenantListItem>(`/api/superadmin/pharmacies/${id}/extend`, request),
    onSuccess: () => invalidateTenants(queryClient),
  });
}

/** Requires `?confirm={slug}` server-side — pass the slug the operator typed. */
export function useDeleteTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, confirmSlug }: { id: string; confirmSlug: string }) =>
      api.delete<void>(`/api/superadmin/pharmacies/${id}`, { confirm: confirmSlug }),
    onSuccess: () => invalidateTenants(queryClient),
  });
}

export function usePurgeTenant() {
  return useMutation({
    mutationFn: ({ slug, confirmSlug }: { slug: string; confirmSlug: string }) =>
      api.post<void>(`/api/superadmin/tenants/${slug}/purge`, undefined, { confirm: confirmSlug }),
  });
}

export function useSeedDemo() {
  return useMutation({
    mutationFn: (slug: string) => api.post<DemoSeedResult>(`/api/superadmin/tenants/${slug}/seed-demo`),
  });
}

export function useClearDemo() {
  return useMutation({
    mutationFn: (slug: string) => api.post<DemoSeedResult>(`/api/superadmin/tenants/${slug}/clear-demo`),
  });
}

// ── Payment claims ───────────────────────────────────────────────────────────

interface ClaimsQuery {
  status?: string;
  page?: number;
  limit?: number;
  [key: string]: string | number | boolean | null | undefined;
}

export function useAdminClaims(query: ClaimsQuery = {}) {
  return useQuery({
    queryKey: ['superadmin', 'payment-claims', query],
    queryFn: () => api.get<PagedResult<PaymentClaim>>('/api/superadmin/payment-claims', query),
  });
}

export function useApproveClaim() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: ApproveClaimRequest }) =>
      api.post<ApproveClaimResponse>(`/api/superadmin/payment-claims/${id}/approve`, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['superadmin', 'payment-claims'] });
      invalidateTenants(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['superadmin', 'stats'] });
    },
  });
}

export function useRejectClaim() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: RejectClaimRequest }) =>
      api.post<PaymentClaim>(`/api/superadmin/payment-claims/${id}/reject`, request),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['superadmin', 'payment-claims'] }),
  });
}

// ── Payment settings ─────────────────────────────────────────────────────────

export function useAdminPaymentSettings() {
  return useQuery({
    queryKey: ['superadmin', 'payment-settings'],
    queryFn: () => api.get<PaymentInfo>('/api/superadmin/payment-settings'),
  });
}

export function useUpdatePaymentSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: UpdatePaymentSettingsRequest) =>
      api.put<PaymentInfo>('/api/superadmin/payment-settings', request),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['superadmin', 'payment-settings'] }),
  });
}

// ── Platform stats ───────────────────────────────────────────────────────────

export function usePlatformStats() {
  return useQuery({
    queryKey: ['superadmin', 'stats'],
    queryFn: () => api.get<PlatformStats>('/api/superadmin/stats'),
    staleTime: 30_000,
  });
}

export function useTenantIsolation(enabled = false) {
  return useQuery({
    queryKey: ['superadmin', 'tenant-isolation'],
    queryFn: () => api.get<TenantIsolation>('/api/superadmin/tenant-isolation'),
    enabled,
  });
}

// ── Platform audit log ───────────────────────────────────────────────────────

interface AuditQuery {
  eventName?: string;
  slug?: string;
  page?: number;
  limit?: number;
  [key: string]: string | number | boolean | null | undefined;
}

export function useAdminAuditLog(query: AuditQuery = {}) {
  return useQuery({
    queryKey: ['superadmin', 'audit-log', query],
    queryFn: () => api.get<PagedResult<PlatformAuditEntry>>('/api/superadmin/audit-log', query),
  });
}

// ── Unverified users ─────────────────────────────────────────────────────────

export function useUnverifiedUsers() {
  return useQuery({
    queryKey: ['superadmin', 'users', 'unverified'],
    queryFn: () => api.get<UnverifiedUser[]>('/api/superadmin/users/unverified'),
  });
}

export function useVerifyUserEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      api.post<void>('/api/superadmin/users/verify-email', { email }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['superadmin', 'users', 'unverified'] }),
  });
}
